import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { motion } from 'framer-motion'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined })
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-center min-h-[60vh]"
        >
          <div className="text-center max-w-md space-modern">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-r from-destructive to-destructive/80 flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-primary-foreground" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground">Something went wrong</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-6">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            {this.state.error && (
              <details className="text-left mb-6">
                <summary className="cursor-pointer text-sm font-medium mb-2">Error Details</summary>
                <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={this.handleRetry}
              className="btn btn-primary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </motion.button>
          </div>
        </motion.div>
      )
    }

    return this.props.children
  }
} 